import { ReactNode, useMemo } from 'react';

import createSafeContext from '#/utils/createSafeContext';

import { AccountState, useAccountState } from './account-context';
import {
  accountValueView,
  healthFactorView,
  initialMarginRequirementView,
  maintenanceMarginRequirementView,
} from './account-views';

export const [useMarginSummaryView, MarginSummaryViewCtx] =
  createSafeContext<MarginSummaryView>('MarginSummaryView');

interface ProviderProps {
  readonly children: ReactNode;
}

export function MarginSummaryViewProvider(props: ProviderProps) {
  const accountState = useAccountState();

  const view = useMemo(() => createView(accountState), [accountState]);

  return (
    <MarginSummaryViewCtx value={view}>{props.children}</MarginSummaryViewCtx>
  );
}

export interface MarginSummaryView {
  readonly accountValue: string;
  readonly initialMarginRequirement: string;
  readonly maintenanceMarginRequirement: string;
  readonly healthFactor: string;
}

const emptyView: MarginSummaryView = {
  accountValue: '-',
  initialMarginRequirement: '-',
  maintenanceMarginRequirement: '-',
  healthFactor: '-',
};

export function createView(state: AccountState): MarginSummaryView {
  const { account } = state;

  if (account == null) return emptyView;

  const accountValue = accountValueView(account);

  const initialMarginRequirement = initialMarginRequirementView(account);

  const maintenanceMarginRequirement =
    maintenanceMarginRequirementView(account);

  const healthFactor = healthFactorView(account);

  return {
    accountValue,
    initialMarginRequirement,
    maintenanceMarginRequirement,
    healthFactor,
  };
}
